// EVERY SHOP DRAWING ON ONE SCHEDULE ACTIVITY, AND WHAT STATE IT IS IN.
//
// Owner, 2026-10-08: "clicking on any schedule activity, will produce the side
// right menu/ drawer, make sure every draweer per activity on the schedule
// also lists all shop drawings of that area/activity and their state
// (delivered, pending ...)".
//
// WHY THE SERVER DECIDES THE WORDS. The states come back already phrased -
// "not delivered", "delivered, awaiting review", "delivered and validated" -
// from back/utils/activityShopDrawings, which is also what the schedule's
// refusal and the drawings tab read. A component that mapped statuses to its
// own labels would be a second vocabulary, and the first time somebody changed
// one the drawer and the refusal would describe the same area differently.
//
// IT SHOWS THE REFUSAL TOO, so a reviewer reading the drawer already knows
// what the sign-off is going to say rather than finding out by pressing it.
import React, { useEffect, useState } from "react";
import { Tag, Empty, Spin, Alert } from "antd";
import { url, tokenRef } from "api";
import ActivityPathLine from "pages/ShopDrawings/ActivityPathLine";

// Green for delivered and validated, amber for delivered but not yet judged,
// red for the ones that hold an area up. The colour carries the same
// three-way distinction as the words, for scanning a long list.
const COLOUR = {
  approved: "green",
  submitted: "gold",
  under_review: "gold",
  requested: "red",
  rejected: "red",
};

export default function ActivityShopDrawings({ projectId, ganttItemId }) {
  const [state, setState] = useState({ loading: true, data: null, error: "" });

  useEffect(() => {
    if (!projectId || !ganttItemId) return;
    let alive = true;
    setState({ loading: true, data: null, error: "" });
    fetch(`${url}/projects/${projectId}/gantt-items/${ganttItemId}/shop-drawings`, {
      headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
    })
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        // NO VIEW KEY (2026-10-14 CRUD keys): said in the person's words,
        // not as a failed read - nothing is wrong, they are not given it.
        if (r.status === 403 && body.missingPermission) {
          throw new Error("you do not have permission to view shop drawings on this project");
        }
        if (!r.ok) throw new Error(body.err || `HTTP ${r.status}`);
        return body;
      })
      // GUARDED AGAINST A LATE REPLY. The drawer is opened by clicking rows,
      // so a slow answer for the row before this one would otherwise paint
      // over the row the person is actually looking at.
      .then((data) => { if (alive) setState({ loading: false, data, error: "" }); })
      .catch((e) => { if (alive) setState({ loading: false, data: null, error: e.message || "" }); });
    return () => { alive = false; };
  }, [projectId, ganttItemId]);

  if (state.loading) return <Spin size="small" />;
  // SAID, NOT SWALLOWED. A failed read must not look like an activity with no
  // drawings - that is the reading that would let somebody sign off an area
  // believing nothing is outstanding.
  if (state.error) {
    return <div style={{ color: "#B84B0A", fontSize: 12 }}>Could not read the shop drawings ({state.error})</div>;
  }
  const d = state.data || {};
  const rows = d.drawings || [];
  // WHICH ACTIVITY THESE HANG OFF, as the digest prints it (owner, 2026-10-13).
  const where = d.activity && d.activity.fullPath
    ? <ActivityPathLine activity={d.activity} style={{ marginBottom: 6 }} />
    : null;
  if (!rows.length) {
    return (
      <div>
        {where}
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No shop drawings registered against this activity" />
      </div>
    );
  }

  return (
    <div>
      {where}
      <div style={{ fontSize: 12, color: "#667085", marginBottom: 8 }}>
        {d.delivered} of {d.total} delivered
        {d.approved ? ` · ${d.approved} validated` : ""}
        {d.outstanding ? ` · ${d.outstanding} outstanding` : ""}
      </div>
      {d.refusal ? (
        <Alert type="warning" showIcon style={{ marginBottom: 8, fontSize: 12 }} message={d.refusal} />
      ) : null}
      {rows.map((r) => (
        <div key={r._id} style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 6 }}>
          <a href={`/shop-drawings?projectId=${projectId}&drawing=${r._id}`}
             target="_blank" rel="noopener noreferrer"
             style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {r.sheetNumber ? <b>{r.sheetNumber}</b> : null} {r.name}
            {r.revision ? <span style={{ color: "#667085" }}> · rev {r.revision}</span> : null}
          </a>
          <Tag color={COLOUR[r.status] || "default"} style={{ marginRight: 0 }}>{r.says}</Tag>
        </div>
      ))}
    </div>
  );
}
